import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as moment from 'moment-timezone';
import { UserIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import api from '../api/axios';
import MetricCard from '../components/MetricCard';
import Table from '../components/Table';
import { formatMinutes } from '../utils';

export default function ProfilePage() {
    const { t, i18n } = useTranslation();
    const query = new URLSearchParams(window.location.search);
    const uId = query.get('u');

    const [userInfo, setUserInfo] = useState(() => JSON.parse(localStorage.getItem('user')) || {});
    const companyId = localStorage.getItem('companyId');
    const userId = uId || userInfo.systemId || userInfo._id;

    const [loadingMetrics, setLoadingMetrics] = useState(false);
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingSummary, setLoadingSummary] = useState(false);
    const [error, setError] = useState(null);

    const [userDetails, setUserDetails] = useState(null);
    const [currentMonthData, setCurrentMonthData] = useState(null);
    const [last3MonthsRecords, setLast3MonthsRecords] = useState([]);
    const [last3DaysEvents, setLast3DaysEvents] = useState([]);

    const timezone = 'Asia/Tashkent';

    useEffect(() => {
        if (!companyId || !userId) return;

        const fetchData = async () => {
            setError(null);

            // Fetch User Details to show name
            try {
                const userRes = await api.get(`/companies/${companyId}/user/${userId}`);
                const fetchedUser = userRes.data?.data || userRes.data;
                setUserDetails(fetchedUser);
                setUserInfo(prev => ({ ...prev, ...fetchedUser }));
            } catch (err) {
                console.error("Failed to fetch user details", err);
            }

            // 1. Monthly Records (Last 3 Months)
            setLoadingSummary(true);
            setLoadingMetrics(true);
            try {
                const now = moment.tz(timezone);
                const startRange = now.clone().subtract(2, 'months').startOf('month').utc().toISOString();
                const endRange = now.clone().endOf('month').utc().toISOString();

                const summaryUrl = `/metrics/${companyId}/associateMonthly?startDate=${startRange}&endDate=${endRange}&userId=${encodeURIComponent(userId)}`;
                const res = await api.get(summaryUrl);
                const data = res.data?.data || [];

                // Sort by date descending
                const sorted = [...data].sort((a, b) => new Date(b.DateLocal) - new Date(a.DateLocal));
                setLast3MonthsRecords(sorted);

                // Extract Current Month for top metrics
                const currentMonthStr = now.format('YYYY-MM');
                const currentRecord = data.find(r => r.DateLocal.startsWith(currentMonthStr));
                setCurrentMonthData(currentRecord || null);
            } catch (err) {
                console.error("Failed to load monthly summary", err);
            } finally {
                setLoadingSummary(false);
                setLoadingMetrics(false);
            }

            // 2. 31 days Event Logs
            setLoadingEvents(true);
            try {
                const now = moment.tz(timezone);
                const startRange = now.clone().subtract(31, 'days').startOf('day').utc().toISOString();
                const endRange = now.clone().endOf('day').utc().toISOString();

                const eventsUrl = `/events/${companyId}/log?startDate=${startRange}&endDate=${endRange}&userId=${encodeURIComponent(userId)}`;
                const res = await api.get(eventsUrl);
                const data = res.data?.data || [];
                data.sort((a, b) => new Date(b.checkInTimeUTC) - new Date(a.checkInTimeUTC));
                data.forEach(d => {
                    d.shift.name = `${d.shift.name} (${d.shift.startDateString || 'N/A'} - ${d.shift.endDateString || 'N/A'})`
                })
                setLast3DaysEvents(data);
            } catch (err) {
                console.error("Failed to load event logs", err);
            } finally {
                setLoadingEvents(false);
            }
        };

        fetchData();
    }, [companyId, userId]);

    const metrics = [
        { title: t('totalHours'), value: formatMinutes(currentMonthData?.minutesWorked) || '—', color: 'text-blue-400' },
        { title: t('lateDays'), value: currentMonthData?.lateDays || 0, color: 'text-yellow-400' },
        { title: t('noDataDays'), value: currentMonthData?.noDataDays || 0, color: 'text-red-400' },
        { title: t('totalChargePercentage'), value: currentMonthData?.charge?.totalChargeForMonth ? `${currentMonthData.charge.totalChargeForMonth.toFixed(1)}%` : '0%', color: 'text-orange-400' }
    ];

    return (
        <div className="p-6 text-white min-h-screen bg-gray-900 space-y-10">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
                <div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="text-3xl font-bold text-blue-400">
                            {userDetails ? `${userDetails.firstName} ${userDetails.lastName}` : (t('profile') || 'Profile')}
                        </h1>
                        {userInfo.telegram_user ? (
                            <div className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/20 border border-green-500/30 text-green-400 text-sm font-medium">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                                </svg>
                                Telegram Connected
                                <div className="relative group">
                                    <InformationCircleIcon className="w-4 h-4 cursor-help ml-1" />
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-gray-200 text-xs rounded-lg whitespace-nowrap border border-gray-700 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                        If you haven't connected, please address this to your manager
                                    </div>
                                </div>
                            </div>
                        ) : userInfo.uniqueId ? (
                            <a
                                href={`https://t.me/tranzact_robot?start=u${userInfo.uniqueId}`}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                                </svg>
                                Connect Telegram
                            </a>
                        ) : null}
                    </div>
                    <p className="text-gray-400 mt-1">
                        {userDetails?.positionTitle || userDetails?.role?.name || ''} • ID: {userId}
                    </p>
                </div>
            </div>

            {/* Top Metrics Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {metrics.map((m, idx) => (
                    <MetricCard
                        key={idx}
                        title={m.title}
                        value={m.value}
                        color={m.color}
                        subtitle={t('currentMonth') || 'Current Month'}
                    />
                ))}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-1 gap-8">
                {/* Monthly Summary (Last 3 Months) */}
                <div className="bg-gray-800/40 p-1 rounded-2xl border border-gray-800 space-y-4">
                    <div className="px-5 pt-5 pb-1 flex justify-between items-center">
                        <h2 className="text-xl font-semibold text-gray-200">{t('monthlySummary') || 'Monthly Summary'}</h2>
                    </div>
                    <Table
                        data={last3MonthsRecords}
                        columns={[
                            { header: t('date'), accessor: 'DateLocal', sortable: true },
                            { header: t('totalHours'), accessor: 'minutesWorked', render: (row) => formatMinutes(row.minutesWorked) },
                            { header: t('lateDays'), accessor: 'lateDays', align: 'right' },
                            { header: t('noDataDays'), accessor: 'noDataDays', align: 'right' },
                            { header: t('totalChargePercentage'), accessor: 'charge.totalChargeForMonth', align: 'right', render: (row) => `${row.charge?.totalChargeForMonth?.toFixed(1) || 0}%` }
                        ]}
                        emptyMessage={t('noDataFound')}
                        containerClassName="bg-transparent overflow-hidden"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-1 gap-8">
                {/* Event Logs */}
                <div className="bg-gray-800/40 p-1 rounded-2xl border border-gray-800 space-y-4">
                    <div className="px-5 pt-5 pb-1 flex justify-between items-center">
                        <h2 className="text-xl font-semibold text-gray-200">{t('eventLogs')}</h2>
                    </div>
                    <Table
                        data={last3DaysEvents}
                        columns={[
                            { header: t('checkIn'), accessor: 'checkInLocal', className: "whitespace-nowrap" },
                            { header: t('checkOut'), accessor: 'checkOutLocal', className: "whitespace-nowrap" },
                            { header: t('workedHours'), accessor: 'minutesWorked', align: 'right', render: (row) => formatMinutes(row.minutesWorked) },
                            { header: t('lateMinutes'), accessor: 'shift.lateMinutes', align: 'right', render: (row) => row.shift?.lateMinutes || 0 },
                            { header: t('shift'), accessor: 'shift.name', align: 'right', className: "font-medium text-gray-300" },
                            {
                                header: t('status'),
                                align: 'right',
                                render: (row) => {
                                    const late = row.shift?.lateMinutes || 0;
                                    if (late > 0) return <span className="text-red-400 font-medium">{t('late')} ({late}{t('minutes')})</span>;
                                    return <span className="text-green-400 font-medium">{t('onTime')}</span>;
                                }
                            }
                        ]}
                        emptyMessage={t('noDataFound')}
                        containerClassName="bg-transparent overflow-hidden"
                    />
                </div>
            </div>
        </div>
    );
}
